import { API } from "apis";
import CategoryBreadcrumb from "components/category/CategoryBreadcrumb";
import CategoryEditDialog from "components/category/CategoryEditDialog";
import PageTitle from "components/common/PageTitle";
import ConfirmDialog from "components/dialog/ConfirmDIalog";
import { CategoryType } from "enums";
import { CategoryModel, ContextModel } from "models";
import { CommonFileModel } from "models/CommonFileModel";
import React from "react";
import { Helmet } from "react-helmet-async";
import { BsTrash } from "react-icons/bs";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useOutletContext } from "react-router-dom";
import { Utils } from "utils";

const CategoryCorpPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const [original, setOriginal] = React.useState<CategoryModel[] | null>(null);
  const [data, setData] = React.useState<CategoryModel[]>();
  const [breadcrumb, setBreadcrumb] = React.useState<CategoryModel[]>([]);
  const [list, setList] = React.useState<CategoryModel[]>([]);

  const [confirmDlg, setConfirmDlg] = React.useState<{
    message: string;
    callback: (result: boolean) => void;
  } | null>(null);
  const [editDlg, setEditDlg] = React.useState<{
    title: string;
    target?: CategoryModel;
    breadcrumb: CategoryModel[];
    callback: (data?: {
      target?: CategoryModel;
      breadcrumb: CategoryModel[];
      name: string;
      image?: CommonFileModel;
    }) => void;
  } | null>(null);

  const _reload = async () => {
    try {
      const response = await API.CategoryCorp.list();
      setData(response.data);
      const arr = breadcrumb.map((v) => {
        return Utils.Category.pick(v.id, response.data);
      }) as CategoryModel[];
      setBreadcrumb(arr);
      if (arr.length > 0) {
        setList(arr[arr.length - 1].list!);
      } else {
        setList(response.data);
      }
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
  };

  const _updateList = (breadcrumb: CategoryModel[]) => {
    setBreadcrumb(breadcrumb);
    if (breadcrumb.length > 0) {
      const last = breadcrumb[breadcrumb.length - 1];
      setList(last.list);
    } else {
      setList(data!);
    }
  };

  // 選択
  const onSelect = async (item: CategoryModel) => {
    if (item.type !== CategoryType.LARGE) {
      return;
    }
    const tmp = [...breadcrumb];
    tmp.push(item);
    setBreadcrumb(tmp);
    setList(item.list);
  };

  // 新規作成
  const onCreate = async (data?: {
    breadcrumb: CategoryModel[];
    name: string;
    image?: CommonFileModel;
  }) => {
    if (!data) {
      setEditDlg(null);
      return;
    }

    if (breadcrumb.length === 0) {
      ctx.loading.setState(true);
      try {
        const response = await API.CategoryCorp.create({
          name: data.name,
          image: data.image!,
        });
        console.log(response);
        await _reload();
        // setEditDlg(null);
        setEditDlg({
          title: "会社カテゴリ新規登録",
          breadcrumb,
          callback: onCreate,
        });

        ctx.msgDialog.setState({
          message: data.name + "の登録が完了しました。",
        });
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    } else if (breadcrumb.length === 1) {
      ctx.loading.setState(true);
      try {
        const response = await API.CategoryCorp.create({
          name: data.name,
          parent_id: breadcrumb[0].id,
        });
        console.log(response);
        await _reload();
        // setEditDlg(null);
        setEditDlg({
          title: "会社カテゴリ新規登録",
          breadcrumb,
          callback: onCreate,
        });

        ctx.msgDialog.setState({
          message: data.name + "の登録が完了しました。",
        });
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    }
  };
  // 編集
  const onEdit = async (data?: {
    target?: CategoryModel;
    breadcrumb: CategoryModel[];
    name: string;
    image?: CommonFileModel;
  }) => {
    if (!data || !data.target) {
      setEditDlg(null);
      return;
    }
    ctx.loading.setState(true);
    try {
      const response = await API.CategoryCorp.update(
        data.target,
        data.breadcrumb,
        data.name,
        data.image
      );
      console.log(response);
      await _reload();
      // setEditDlg(null);

      ctx.msgDialog.setState({
        message: data.name + "の更新が完了しました。",
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };
  // 削除
  const onDelete = (item: CategoryModel) => {
    console.log(item);
    setConfirmDlg({
      message: `${item.name}を削除します。よろしいですか？`,
      callback: async (result: boolean) => {
        if (result) {
          ctx.loading.setState(true);
          try {
            const response = await API.CategoryCorp.del(item, breadcrumb);
            console.log(response);
            await _reload();
            setConfirmDlg(null);
          } catch (err) {
            Utils.errorAction(ctx, err);
            setConfirmDlg(null);
          }
          ctx.loading.setState(false);
        } else {
          setConfirmDlg(null);
        }
      },
    });
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      try {
        await ctx.auth.initialize();

        const response = await API.CategoryCorp.list();
        console.log(response);
        setData(response.data);
        setBreadcrumb([]);
        setList(response.data);
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-full flex flex-col justify-start overflow-y-auto">
      <Helmet>
        <title>会社カテゴリ設定</title>
      </Helmet>
      <PageTitle title="会社カテゴリ設定" className="p-2" />

      <CategoryBreadcrumb
        topLabel="メインカテゴリ一覧"
        data={breadcrumb}
        onUpdate={(breadcrumb) => {
          _updateList(breadcrumb);
        }}
      />

      <div className="mt-4 px-16 flex justify-between">
        {original ? (
          <>
            <button
              className="secondary px-4 py-2"
              onClick={() => {
                list.sort((a, b) => {
                  if (a.sort_no > b.sort_no) return 1;
                  if (a.sort_no < b.sort_no) return -1;
                  return 0;
                });
                setList([...list]);
                setData(original);
                setOriginal(null);
              }}
            >
              キャンセル
            </button>
            <button
              className="secondary px-4 py-2"
              onClick={async () => {
                if (!data) {
                  return;
                }
                ctx.loading.setState(true);
                try {
                  const response = await API.CategoryCorp.sort(data);
                  console.log(response);
                  setOriginal(null);
                } catch (err) {
                  Utils.errorAction(ctx, err);
                }
                ctx.loading.setState(false);
              }}
            >
              確定
            </button>
          </>
        ) : (
          <>
            <button
              className ={`${list.length <= 1 ? 'secondary-disabled px-4 py-2': 'secondary px-4 py-2'}`}
              onClick={() => {
                const copy = JSON.parse(JSON.stringify(data));
                setOriginal(copy);
              }}
              disabled={list.length <= 1}
            >
              表示順変更
            </button>
            <button
              className="secondary px-4 py-2"
              onClick={() =>
                setEditDlg({
                  title: "会社カテゴリ新規登録",
                  breadcrumb,
                  callback: onCreate,
                })
              }
            >
              新規登録
            </button>
          </>
        )}
      </div>

      <div className="flex-1 px-16 py-8 overflow-y-auto">
        <div className="border border-black mx-auto h-full overflow-y-auto">
          {list.map((v, i) => (
            <div
              key={i}
              className="flex items-center border border-gray-600 bg-white"
              draggable={!!original}
              onDragStart={(e) => {
                e.dataTransfer.clearData();
                e.dataTransfer.setData("text/plain", v.id);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const dragID = e.dataTransfer.getData("text/plain");
                if (!data) {
                  return;
                }
                const drag = Utils.Category.pick(dragID, data);
                if (!drag) {
                  return;
                }
                const drop = v;
                const root =
                  breadcrumb.length === 0
                    ? data
                    : breadcrumb[breadcrumb.length - 1].list;
                const idxDrag = root.indexOf(drag);
                const idxDrop = root.indexOf(drop);
                root.splice(idxDrag, 1);
                root.splice(idxDrop, 0, drag);
                setList([...root]);
              }}
            >
              <div
                className="px-4 cursor-pointer flex-1"
                onClick={() => {
                  onSelect(v);
                }}
              >
                {v.name}
              </div>
              <div
                className="border-l border-black p-4 cursor-pointer"
                onClick={() => {
                  onDelete(v);
                }}
              >
                <BsTrash className="text-xl" />
              </div>
              <div
                className="border-l border-black p-4 cursor-pointer"
                onClick={() => {
                  setEditDlg({
                    title: "会社カテゴリ更新",
                    target: v,
                    breadcrumb,
                    callback: onEdit,
                  });
                }}
              >
                <MdOutlineModeEditOutline className="text-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <CategoryEditDialog ctx={ctx} data={editDlg} />
      <ConfirmDialog
        modalIsOpen={!!confirmDlg}
        message={confirmDlg?.message}
        action={confirmDlg?.callback}
      />
    </div>
  );
};
export default CategoryCorpPage;
